import * as React from "react";
import { Box, TextField, Autocomplete, Grid, Typography } from "@common-ui/ui";
import { LocationOn } from "@mui/icons-material";

import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";
import throttle from "lodash/throttle";

import { places } from "../../services";
import { updateOrgForm } from "../../utils";

export default function Address({
  addressType = "Label",
  formik,
  field,
  disabled = false,
}) {
  const [inputValue, setInputValue] = React.useState("");
  const [options, setOptions] = React.useState([]);

  const fetch = React.useMemo(
    () =>
      throttle(async (request, callback) => {
        try {
          let payload = await places(request.input);

          callback(payload.Results);
        } catch (error) {
          console.log(error);
        }
      }, 200),
    []
  );

  React.useEffect(() => {
    let active = true;

    if (inputValue === "") {
      setOptions(formik.values[field] ? [formik.values[field]] : []);
      return undefined;
    }
    fetch({ input: inputValue }, (results) => {
      if (active) {
        let newOptions = [];

        if (formik.values[field]) {
          newOptions = [formik.values[field]];
        }

        if (results) {
          newOptions = [...newOptions, ...results];
        }

        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values[field], inputValue, fetch]);

  return (
    <div
      onBlur={() => {
        if (!formik.values[field]) formik.setFieldValue(field, inputValue);
      }}
    >
      <Autocomplete
        id="address-auto-complete"
        sx={{ mt: 1 }}
        freeSolo
        disabled={disabled}
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option?.Place[addressType]
        }
        placeholder="Type"
        size="small"
        filterOptions={(x) => x}
        options={options}
        autoComplete
        includeInputInList
        filterSelectedOptions
        value={formik?.values[field] || ""}
        onChange={(event, newValue) => {
          setOptions(newValue ? [newValue, ...options] : options);
          updateOrgForm(formik, field, newValue);
        }}
        onInputChange={async (event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        margin="normal"
        renderInput={(params) => (
          <TextField
            placeholder="Type"
            error={Boolean(formik.touched[field] && formik.errors[field])}
            fullWidth
            helperText={formik.touched[field] && formik.errors[field]}
            name={field}
            variant="outlined"
            inputProps={{
              ...params.inputProps,
              autoComplete: "off",
            }}
            {...params}
          />
        )}
        renderOption={(props, option) => {
          const matches = match(option?.Place?.Label || "", inputValue);
          const parts = parse(option?.Place?.Label || "", matches || []) || [];

          return (
            <li {...props}>
              <Grid container alignItems="center">
                <Grid item>
                  <Box
                    component={LocationOn}
                    sx={{ color: "text.secondary", mr: 2 }}
                  />
                </Grid>
                <Grid item xs>
                  {parts.map((part, index) => (
                    <span
                      key={index}
                      style={{
                        fontWeight: part.highlight ? 700 : 400,
                      }}
                    >
                      {part?.text}
                    </span>
                  ))}

                  <Typography variant="body2" color="text.secondary">
                    {`${
                      option?.Place?.SubRegion
                        ? option?.Place?.SubRegion + ", "
                        : ""
                    }${
                      option?.Place?.Region ? option?.Place?.Region + ", " : ""
                    }${option?.Place?.Country || ""}`}
                  </Typography>
                </Grid>
              </Grid>
            </li>
          );
        }}
      />
    </div>
  );
}
